import { ArrowRight, MapPin, Package, Scales, Clock, Path } from "@phosphor-icons/react";

const EQUIP_COLOR = {
  Expedited: "bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/30",
  Hotshot: "bg-[#60A5FA]/10 text-[#60A5FA] border-[#60A5FA]/40",
  "Dry Van": "bg-slate-500/10 text-slate-300 border-slate-500/30",
  Flatbed: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  Reefer: "bg-teal-500/10 text-teal-300 border-teal-500/30",
  OTR: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
};

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export const LoadCard = ({ load, onRequest, index = 0 }) => {
  const booked = load.status !== "open";
  return (
    <div
      data-testid={`load-card-${load.id}`}
      className="group relative bg-[#111A2E] border border-white/10 rounded-xl p-6 transition-transform duration-300 hover:-translate-y-1 hover:border-[#2563EB]/40 animate-fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${EQUIP_COLOR[load.equipment] || EQUIP_COLOR["Dry Van"]}`}>
              {load.equipment}
            </span>
            <span className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${booked ? "bg-slate-600/20 text-slate-400 border-slate-600/40" : "bg-green-500/10 text-green-400 border-green-500/30"}`} data-testid={`load-status-${load.id}`}>
              {booked ? load.status.charAt(0).toUpperCase() + load.status.slice(1) : "Open"}
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-display text-xl sm:text-2xl font-bold tracking-tight text-white flex items-center gap-2">
              <MapPin size={18} className="text-[#2563EB] shrink-0" />
              {load.origin_city}, {load.origin_state}
            </div>
            <ArrowRight size={22} className="text-[#2563EB] shrink-0" weight="bold" />
            <div className="font-display text-xl sm:text-2xl font-bold tracking-tight text-white">
              {load.dest_city}, {load.dest_state}
            </div>
          </div>

          <div className="flex items-center gap-5 mt-3 text-sm text-[#94A3B8] flex-wrap">
            {load.miles != null && <span className="flex items-center gap-1.5"><Path size={15} /> {load.miles} mi</span>}
            {load.deadhead != null && <span className="flex items-center gap-1.5 text-[#64748b]">DH {load.deadhead} mi</span>}
            {load.weight_lbs != null && <span className="flex items-center gap-1.5"><Scales size={15} /> {load.weight_lbs.toLocaleString()} lbs</span>}
            {load.commodity && <span className="flex items-center gap-1.5"><Package size={15} /> {load.commodity}</span>}
          </div>

          <div className="flex items-center gap-5 mt-2 text-xs text-[#64748b] flex-wrap">
            <span>Pickup {load.pickup_date}</span>
            <span>Delivery {load.delivery_date}</span>
            <span className="flex items-center gap-1"><Clock size={13} /> {timeAgo(load.created_at)}</span>
          </div>
        </div>

        <div className="flex flex-row lg:flex-col items-end justify-between lg:justify-center gap-3 lg:min-w-[180px] lg:border-l lg:border-white/10 lg:pl-6">
          <div className="text-right">
            <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-[#10B981]" data-testid={`load-rate-${load.id}`}>
              ${load.rate_usd.toLocaleString()}
            </div>
            {load.rate_per_mile != null && (
              <div className="text-xs text-[#94A3B8] mt-0.5">${load.rate_per_mile.toFixed(2)}/mi</div>
            )}
          </div>
          <button
            data-testid={`request-load-btn-${load.id}`}
            disabled={booked}
            onClick={() => onRequest(load)}
            className="px-5 py-2.5 rounded-full bg-[#2563EB] text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {booked ? "Booked" : "Request Load"}
          </button>
        </div>
      </div>
    </div>
  );
};
